import type { ComponentType, SVGProps } from "react";
import { Box, Flex, Hide, Text, useToken, VStack } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { listingsModel } from "models/listings";
import type { FeaturePreferenceKey } from "models/types/agency-settings";
import { useParams } from "react-router-dom";
import { ContactItem } from "components/left-navigation-menu/components/contact-item";
import { LeftNavigationMenuItem } from "components/left-navigation-menu/components/left-navigation-menu-item";
import { PortalSelector } from "components/portal-selector";
import { PropertySelector } from "components/property-selector";
import { getTopNavigationBarHeight } from "components/top-navigation-bar";
import { useIsAgencyFeaturePreferenceEnabled } from "hooks/use-is-agency-feature-preference-enabled";
import { usePortalPath } from "hooks/use-portal-path";
import { useRegion } from "hooks/use-region";

export type MenuItem = {
  label: string;
  path: string;
  Icon?: ComponentType<SVGProps<SVGSVGElement>>;
  featureKey?: FeaturePreferenceKey;
  isLocked?: boolean;
  isLockedTooltip?: string;
  regionVisibility?: MenuItemRegionVisibility;
};

export type MenuItemRegionVisibility = {
  GBR?: boolean;
  AUS?: boolean;
};

export const isVisibleInRegion = (
  menuItem: MenuItem,
  region: keyof MenuItemRegionVisibility,
): boolean => {
  const regionVisibility = menuItem.regionVisibility;

  if (!regionVisibility) {
    return true;
  }

  return regionVisibility[region] ?? true;
};

function ListingSelection() {
  return (
    <Hide below={"sm"}>
      <Flex direction={"column"} w={"100%"} mb={"40px"}>
        <PortalSelector
          overlineText={"Property"}
          menuButtonProps={{
            variant: "ghost",
            fontSize: "2xl",
            w: "100%",
            textAlign: "left",
            color: "client.navigation.text.primary",
            _hover: {
              backgroundColor: "client.navigation.hover.background",
              color: "client.navigation.hover.text",
            },
            _active: {
              backgroundColor: "client.navigation.hover.background",
              borderColor: "client.navigation.active.background",
              color: "client.navigation.hover.text",
            },
          }}
          overLineTextColor={"client.navigation.text.secondary"}
        />
      </Flex>
    </Hide>
  );
}

function AgentContacts() {
  const { propertyId } = useParams();
  const { data: listing } = useQuery(listingsModel.queries.read(propertyId));
  const borderColor = useToken("colors", "client.navigation.divider");

  return listing?.agency_contacts?.length ? (
    <VStack
      spacing={2}
      mt={"auto"}
      borderTop={`1px solid ${borderColor}`}
      gap={4}
      pt={4}
      justifyContent={"flex-start"}
    >
      <Box w={"100%"}>
        <Text
          fontSize={"14px"}
          fontWeight={500}
          color={"client.navigation.text.primary"}
        >
          Contact
        </Text>
      </Box>
      <VStack w={"100%"} gap={4}>
        {listing?.agency_contacts?.map((contact: any) => (
          <ContactItem key={contact.id} agent={contact} />
        ))}
      </VStack>
    </VStack>
  ) : null;
}

export type LeftNavigationMenuProps = {
  items: MenuItem[];
  hideListingSelector?: boolean;
  hideAgencyContacts?: boolean;
};

const MOBILE_MENU_TOP_MARGIN = 16;

export function LeftNavigationMenu({
  items,
  hideAgencyContacts,
  hideListingSelector,
}: LeftNavigationMenuProps) {
  const isAgencyFeaturePreferenceEnabled =
    useIsAgencyFeaturePreferenceEnabled();
  const px = useToken("space", MOBILE_MENU_TOP_MARGIN);
  const region = useRegion();

  const { isLandlordOrTenantPath } = usePortalPath();

  return (
    <>
      <Box
        w={{ base: "100%", lg: "320px" }}
        bg={"client.navigation.background"}
        pos={"fixed"}
        top={0}
        bottom={0}
        p={{ base: 4, md: 6 }}
        pt={{ base: 4, md: 10 }}
        mt={{ base: MOBILE_MENU_TOP_MARGIN, lg: getTopNavigationBarHeight() }}
        display={"flex"}
        flexDir={"column"}
        alignItems={"space-between"}
        overflowY={{ base: "scroll", lg: "visible" }}
        zIndex={999}
        h={[`calc(${window.innerHeight}px - ${px})`, "unset"]}
      >
        {!hideListingSelector && !isLandlordOrTenantPath && (
          <ListingSelection />
        )}
        {isLandlordOrTenantPath && (
          <PropertySelector
            overlineText={"Property"}
            menuButtonProps={{
              variant: "ghost",
              fontSize: "2xl",
              w: "100%",
              textAlign: "left",
              color: "client.navigation.text.primary",
              _hover: {
                backgroundColor: "client.navigation.hover.background",
                color: "client.navigation.hover.text",
              },
              _active: {
                backgroundColor: "client.navigation.hover.background",
                borderColor: "client.navigation.active.background",
                color: "client.navigation.hover.text",
                py: 2,
                h: "auto",
                colorScheme: "gray",
                borderWidth: "2px",
              },
            }}
            overLineTextColor={"client.navigation.text.secondary"}
          />
        )}
        <Box>
          <VStack spacing={1}>
            {items
              .filter((item) =>
                item.featureKey
                  ? isAgencyFeaturePreferenceEnabled(item.featureKey)
                  : true,
              )
              .filter((item) => isVisibleInRegion(item, region))
              .map((menu) => (
                <LeftNavigationMenuItem
                  path={menu.path as string}
                  label={menu.label}
                  key={menu.path}
                  Icon={menu.Icon}
                  isLocked={menu.isLocked}
                  isLockedTooltip={menu.isLockedTooltip}
                />
              ))}
          </VStack>
        </Box>
        {!hideAgencyContacts && !isLandlordOrTenantPath && <AgentContacts />}
      </Box>
    </>
  );
}
